import { useState } from 'react';
import type { CpiTreeNode } from '@sol-tx-visualizer/tx-decode';

interface Props {
  nodes: CpiTreeNode[];
}

export default function CpiTree({ nodes }: Props) {
  return (
    <div className="p-4 space-y-2 max-h-[600px] overflow-auto">
      {nodes.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center">No instructions found.</div>
      ) : (
        nodes.map((node, i) => <TreeNode key={node.id} node={node} defaultExpanded={i < 3} />)
      )}
    </div>
  );
}

function TreeNode({ node, defaultExpanded = false }: { node: CpiTreeNode; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasChildren = node.children.length > 0;

  const programColors: Record<string, string> = {
    'System Program': 'bg-slate-600',
    'SPL Token': 'bg-emerald-600',
    'Token-2022': 'bg-emerald-700',
    'Compute Budget': 'bg-amber-600',
    'Associated Token Account': 'bg-cyan-600',
    'Jupiter': 'bg-violet-600',
  };
  const colorClass = programColors[node.programName] || 'bg-slate-700';

  return (
    <div>
      <button
        onClick={() => hasChildren && setExpanded(!expanded)}
        className={`flex items-center gap-2 w-full text-left rounded-md px-2 py-1.5 text-sm transition-colors ${
          hasChildren ? 'hover:bg-slate-800 cursor-pointer' : 'cursor-default'
        }`}
        style={{ paddingLeft: `${(node.stackHeight - 1) * 16 + 8}px` }}
      >
        {hasChildren ? (
          <span className="text-slate-400 w-4 text-center">
            {expanded ? '▼' : '▶'}
          </span>
        ) : (
          <span className="w-4" />
        )}

        <span className={`inline-block w-2 h-2 rounded-full ${colorClass}`} />

        <span className="font-medium text-slate-200">
          #{node.index} {node.programName}
        </span>

        {(() => {
          const dd = node.decodedData as { name?: string } | undefined;
          return dd?.name ? (
            <span className="text-xs text-slate-500">{dd.name}</span>
          ) : null;
        })()}

        {typeof node.cuConsumed === 'number' && node.cuConsumed > 0 && (
          <span className="ml-auto text-xs font-mono text-slate-500">
            {node.cuConsumed.toLocaleString()} CU
          </span>
        )}
      </button>

      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} defaultExpanded />
          ))}
        </div>
      )}
    </div>
  );
}
